import React from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useIntl } from "react-intl";
import { buildManualPointsUrl, historySummaryItems } from "./memberLifecycleUtils";

const signedPoints = (points) => {
  const value = Number(points || 0);
  return `${value > 0 ? "+" : ""}${value}`;
};

const MemberDeletionDialog = ({
  member,
  impact,
  loading,
  error,
  open,
  submitting,
  onClose,
  onDelete,
  onDeactivate,
  onMerge,
}) => {
  const intl = useIntl();
  const manualGroups = impact?.manual_points?.classifications || [];
  const historyItems = historySummaryItems(impact?.history);
  const blocked = impact && !impact.can_delete;

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} fullWidth maxWidth="md">
      <DialogTitle>
        {blocked
          ? intl.formatMessage(
              { id: "members.lifecycle.cannotDeleteTitle" },
              { name: member?.name || intl.formatMessage({ id: "members.lifecycle.thisMember" }) },
            )
          : intl.formatMessage({ id: "members.lifecycle.deletePermanently" })}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Stack alignItems="center" py={5} spacing={2}>
            <CircularProgress size={30} />
            <Typography color="text.secondary">
              {intl.formatMessage({ id: "members.lifecycle.checkingHistory" })}
            </Typography>
          </Stack>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && impact?.can_delete && (
          <Alert severity="warning">
            {intl.formatMessage({ id: "members.lifecycle.safeDeleteWarning" }, { name: member?.name })}
          </Alert>
        )}

        {!loading && !error && blocked && (
          <Stack spacing={2.5}>
            <Alert severity="info">
              {intl.formatMessage({ id: "members.lifecycle.blockedDeleteExplanation" })}
            </Alert>

            {manualGroups.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {intl.formatMessage(
                    { id: "members.lifecycle.manualPointsCount" },
                    { count: impact.manual_points.count },
                  )}
                </Typography>
                <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{intl.formatMessage({ id: "members.lifecycle.ranking" })}</TableCell>
                        <TableCell>{intl.formatMessage({ id: "members.lifecycle.season" })}</TableCell>
                        <TableCell align="right">
                          {intl.formatMessage({ id: "members.lifecycle.adjustments" })}
                        </TableCell>
                        <TableCell align="right">{intl.formatMessage({ id: "members.lifecycle.points" })}</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manualGroups.map((group, index) => {
                        const url = buildManualPointsUrl(group, member.id);
                        return (
                          <TableRow key={`${group.classification?.id || "unknown"}-${group.season?.id || index}`}>
                            <TableCell>
                              {group.classification?.name ||
                                intl.formatMessage({ id: "members.lifecycle.deletedRanking" })}
                            </TableCell>
                            <TableCell>
                              {group.season?.year || intl.formatMessage({ id: "members.lifecycle.noSeason" })}
                            </TableCell>
                            <TableCell align="right">{group.adjustments_count}</TableCell>
                            <TableCell align="right">{signedPoints(group.points_total)}</TableCell>
                            <TableCell align="right">
                              {url && (
                                <Button component={RouterLink} to={url} size="small" onClick={onClose}>
                                  {intl.formatMessage({ id: "members.lifecycle.viewPoints" })}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )}

            {historyItems.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {intl.formatMessage({ id: "members.lifecycle.otherAssociatedRecords" })}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {historyItems.map((item) => (
                    <Chip
                      key={item.key}
                      label={intl.formatMessage({ id: item.messageId }, { count: item.count })}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Stack>
            )}

            <Divider />
            <DialogContentText>
              {intl.formatMessage({ id: "members.lifecycle.mergeExplanation" })}
            </DialogContentText>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap" }}>
        <Button onClick={onClose} disabled={submitting}>
          {intl.formatMessage({ id: "Cancelar" })}
        </Button>
        {!loading && !error && blocked && (
          <>
            <Button onClick={onMerge} disabled={submitting}>
              {intl.formatMessage({ id: "members.lifecycle.mergeDuplicate" })}
            </Button>
            {member?.is_active && (
              <Button variant="contained" onClick={onDeactivate} disabled={submitting}>
                {submitting
                  ? intl.formatMessage({ id: "members.lifecycle.deactivating" })
                  : intl.formatMessage({ id: "members.lifecycle.deactivateMember" })}
              </Button>
            )}
          </>
        )}
        {!loading && !error && impact?.can_delete && (
          <Button variant="contained" color="error" onClick={onDelete} disabled={submitting}>
            {submitting
              ? intl.formatMessage({ id: "members.lifecycle.deleting" })
              : intl.formatMessage({ id: "members.lifecycle.deletePermanently" })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MemberDeletionDialog;
